/**
 * Sets an attribute on an object.
 *
 * > setattr({ foo: { bar: 42 }}, ['foo', 'bar'], 69)
 * { foo: { bar: 69 }}
 */
export function setattr(obj: any, bits: string[], value: any): any {
  if (!bits.length) {
    return value;
  }
  return {
    ...obj,
    [bits[0]]: setattr(obj[bits[0]], bits.slice(1), value),
  };
}

/**
 * Gets an attribute from an object.
 *
 * > getattr({ foo: { bar: 42 }}, ['foo', 'bar'])
 * 42
 */
export function getattr(obj: any, bits: string[]): any {
  for (const b of bits) {
    obj = obj[b];
  }
  return obj;
}

/**
 * Used to assert at compile time that a statement is unreachable.
 *
 * For example:
 *
 *  type Option = 'a' | 'b'
 *
 *  function handleOption(o: Option) {
 *    if (o == 'a')
 *      return handleA()
 *    if (o == 'b')
 *      return handleB()
 *    assertUnreachable(o)
 *  }
 */
export function assertUnreachable(x: never, message?: string): never {
  throw new Error((message ? message + ' ' : '') + 'Reached unreachable statement: ' + JSON.stringify(x));
}

/**
 * Catches any exception which might be raised by a promise and returns a
 * tuple of [result, error], where either the result or the error will be
 * undefined:
 *
 *   let [res, error] = await maybe(someApi.get(...))
 *   if (err) {
 *     return `Oh no there was an error: ${err}`
 *   }
 *   console.log('The result:', res)
 *
 * The result is also an object with `res` and `err` fields:
 *
 *   let someResult = await maybe(someApi.get(...))
 *   if (someResult.err) {
 *     return `Oh no there was an error: ${someResult.err}`
 *   }
 *   console.log('The result:', someResult.res)
 */
export type MaybeRes<T, E = any> = [T, E] & { res: T; err: E };

export function maybe<T, E = any>(p: Promise<T>): Promise<MaybeRes<T, E>> {
  return (p as Promise<T>).then(
    res => maybe.accept<T, E>(res),
    err => maybe.reject<E, T>(err),
  );
}

maybe.accept = <T, E = any>(res: T): MaybeRes<T, E> => Object.assign([res, null], { res, err: null }) as any;
maybe.reject = <E, T = any>(err: E): MaybeRes<T, E> => Object.assign([null, err], { res: null, err }) as any;
maybe.unwrap = async <T, E>(p: Promise<MaybeRes<T, E>>) => {
  const [res, err] = await p;
  if (err) {
    throw new Error(err as any);
  }
  return res;
};

/**
 * Gets a random element from an array
 *
 * > getRandom(['a', 'b', 'c'])
 * 'c'
 *
 * > getRandom(['a', 'b', 'c'])
 * 'a'
 */
export const getRandom = <T>(arr: [T]) => arr[Math.floor(Math.random() * arr.length)];

/**
 * Safely call JSON.stringify(...) on an object, catching any possible error.
 * Additionally, if `{ shorten: ... }` is provided, the output will be
 * shortened (rendering it potentially invalid JSON).
 */
export function safeJson(obj: any, opts?: { shorten?: number }) {
  opts = opts || {};
  let res;
  try {
    res = JSON.stringify(obj);
  } catch (e) {
    res = JSON.stringify({
      error: 'safeJson error: ' + e,
      originalObj: '' + obj,
    });
  }
  if (opts.shorten) {
    res = shorten(res, opts.shorten);
  }
  return res;
}

/**
 * Shorten a string.
 *
 * > shorten('abc')
 * 'abc'
 * > shorten('abcdefg', 4)
 * 'ab…fg'
 */
export function shorten(s: string, len: number = 500) {
  if (!s || s.length <= len) {
    return s;
  }

  return s.slice(0, len / 2) + '…' + s.slice(s.length - len / 2);
}

/**
 * Removes undefined keys from 'obj'.
 *
 * > filterUndefined({ foo: 42, bar: undefined })
 * { foo: 42 }
 */
export function filterUndefined<T extends {}>(obj: T): Partial<T> {
  const res = {} as any;
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) {
      res[k] = v;
    }
  });
  return res;
}
/**
 * Pluralizes a string:
 *
 *  > pluralize(1, 'Page')
 *  '1 Page'
 *  > pluralize(2, 'Page')
 *  '2 Pages'
 *  > pluralize(1, 'Ox', 'Oxen')
 *  '1 Ox'
 *  > pluralize(2, 'Ox', 'Oxen')
 *  '2 Oxen'
 *  > pluralize(1, 'is an invite', 'are {} invites')
 *  'is an invite'
 *  > pluralize(2, 'is an invite', 'are {} invites')
 *  'are 2 invites'
 *  > pluralize.exactly(1, 'an invite', 'many invites')
 *  'an invite'
 *  > pluralize.exactly(2, 'an invite', 'many invites')
 *  'many invites'
 *
 * NOTE: There is a sublte "gotcha": if neither the singular nor the plural
 * strings contain '{}', then it will be prepended to the result. This is
 * generally, but not always, useful. In cases where it's undesierable (ex,
 * because the count is being displayed somewhere else), use
 * `pluralize.exactly`.
 */
export function pluralize(count: number | string, singular: string, plural?: string) {
  const shouldPrefix = singular.indexOf('{}') < 0 && (!plural || plural.indexOf('{}') < 0);

  const prefix = shouldPrefix ? '{} ' : '';

  return pluralize.exactly(count, prefix + singular, plural === undefined ? plural : prefix + plural);
}

pluralize.exactly = function (count: number | string, singular: string, plural?: string) {
  if (plural === undefined) {
    plural = singular + 's';
  }
  const formatStr = parseInt(count as any) == 1 ? singular : plural;
  return formatStr.replace('{}', '' + count);
};

/**
 * Format a number.
 *
 * > formatNumber(42)
 * '42'
 * > formatNumber(123456)
 * '123,456'
 * > formatNumber(null)
 * '–'
 */
export const formatNumber = (n: number | null | undefined, nDecimals?: number) => {
  const numFormatter =
    typeof Intl === 'undefined'
      ? null
      : new Intl.NumberFormat('en-IN', {
          maximumFractionDigits: nDecimals ?? undefined,
        });
  return n == null || n == undefined
    ? '–'
    : typeof n !== 'number'
      ? n
      : !numFormatter
        ? typeof nDecimals == 'number'
          ? n.toFixed(nDecimals)
          : '' + n
        : numFormatter.format(n);
};

export const formatPct = (n: number | null | undefined, nDecimals?: number) => {
  return n == null || n == undefined ? '–' : typeof n !== 'number' ? n : formatNumber(n * 100, nDecimals) + '%';
};
